import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { TID } from "@/constants/testIds";
import QuickLeadForm from "@/components/QuickLeadForm";

const SCROLL_KEY = "phc-scroll-popup-seen";
const EXIT_KEY = "phc-exit-popup-seen";

export function ScrollPopup() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname.startsWith("/eoi") || pathname.startsWith("/reserve") || pathname.startsWith("/admin") || pathname.startsWith("/cp") || pathname.startsWith("/event") || pathname.startsWith("/contact") || pathname.startsWith("/strategy-call")) return;
    if (sessionStorage.getItem(SCROLL_KEY)) return;

    let firedAt = null;
    const enteredAt = Date.now();

    const trigger = () => {
      if (firedAt || sessionStorage.getItem(SCROLL_KEY)) return;
      firedAt = Date.now();
      sessionStorage.setItem(SCROLL_KEY, "1");
      setOpen(true);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      const percent = total > 0 ? window.scrollY / total : 0;
      if (percent >= 0.4) trigger();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const dwellTimer = setTimeout(() => trigger(), 20_000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(dwellTimer);
      void enteredAt;
    };
  }, [pathname]);

  if (!open) return null;
  return (
    <PopupShell testid={TID.scrollPopup} onClose={() => setOpen(false)}>
      <div className="p-6 md:p-7 relative">
        <button
          data-testid={TID.scrollPopupClose}
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center bg-forest/10 hover:bg-forest text-forest hover:text-cream transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="eyebrow text-teal">Before you scroll on</div>
        <h3 className="font-display text-2xl md:text-3xl text-forest mt-2 leading-tight pr-8">
          Leave your details. We&apos;ll do the rest.
        </h3>
        <p className="text-sm text-forest/70 mt-2 leading-relaxed">
          A member of our sales team will reach out &mdash; unhurriedly &mdash;
          with plot layouts, phase status and payment plan tailored to what
          you&apos;re looking for.
        </p>
        <div className="mt-5">
          <QuickLeadForm
            source="scroll_popup"
            variant="contact"
            ctaLabel="Send my details"
            onSubmitted={() => setTimeout(() => setOpen(false), 2200)}
          />
        </div>
      </div>
    </PopupShell>
  );
}

export function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/eoi") || pathname.startsWith("/reserve") || pathname.startsWith("/admin") || pathname.startsWith("/cp") || pathname.startsWith("/event") || pathname.startsWith("/contact") || pathname.startsWith("/strategy-call")) return;
    if (sessionStorage.getItem(EXIT_KEY)) return;
    // Only desktop
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(EXIT_KEY)) {
        sessionStorage.setItem(EXIT_KEY, "1");
        setOpen(true);
      }
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [pathname]);

  if (!open) return null;
  return (
    <PopupShell testid={TID.exitPopup} onClose={() => setOpen(false)}>
      <div className="p-6 md:p-7 relative">
        <button
          data-testid={TID.exitPopupClose}
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center bg-forest/10 hover:bg-forest text-forest hover:text-cream transition-colors z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="eyebrow text-teal">Before you go</div>
        <h3 className="font-display text-2xl md:text-3xl text-forest mt-2 leading-tight pr-8">
          Take Chapter III with you.
        </h3>
        <p className="text-sm text-forest/70 mt-2 leading-relaxed">
          Leave your details and our sales team will reach out with plot
          layouts, phase status and the payment plan — no pressure.
        </p>
        <div className="mt-5">
          <QuickLeadForm
            source="exit_intent"
            variant="contact"
            ctaLabel="Send my details"
            onSubmitted={() => setTimeout(() => setOpen(false), 2200)}
          />
        </div>
      </div>
    </PopupShell>
  );
}

function PopupShell({ children, onClose, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[70] bg-void/60 backdrop-blur-sm p-4 flex items-center justify-center animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div
        className="bg-offwhite rounded-2xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto shadow-[0_40px_80px_-30px_rgba(11,74,61,0.7)] fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
